import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CreditsProvider } from "@/contexts/CreditsContext";
import { ToastProvider } from "@/contexts/ToastContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Home } from "@/pages/Home";
import { Login } from "@/pages/Login";
import { SignUp } from "@/pages/SignUp";
import { Dashboard } from "@/pages/Dashboard";
import { Companies } from "@/pages/Companies";
import { News } from "@/pages/News";
import { SavedAnalyses } from "@/pages/SavedAnalyses";
import { Copilot } from "@/pages/Copilot";
import { MarketMovers } from "@/pages/MarketMovers";
import { CompanyDetail } from "@/pages/CompanyDetail";
import { Admin } from "@/pages/Admin";
import { Pricing } from "@/pages/Pricing";
import { Subscription } from "@/pages/Subscription";
import { EarningsCalls } from "@/pages/EarningsCalls";
import { EarningsCalendar } from "@/pages/EarningsCalendar";
import { Filings } from "@/pages/Filings";
import { BlazerexArticlePage } from "@/pages/BlazerexArticle";
import { About } from "@/pages/About";
import { Rankings } from "@/pages/Rankings";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CreditsProvider>
          <ToastProvider>
            <BrowserRouter>
              <div className="min-h-screen flex flex-col">
                <Header />
                <main className="flex-1 overflow-y-auto">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<SignUp />} />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/companies" element={<Companies />} />
                    <Route
                      path="/companies/:ticker"
                      element={<CompanyDetail />}
                    />
                    <Route path="/rankings" element={<Rankings />} />
                    <Route path="/news" element={<News />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/blazerex-article/:articleId" element={<BlazerexArticlePage />} />
                    <Route path="/earnings-calls" element={<EarningsCalls />} />
                    <Route path="/earnings-calendar" element={<EarningsCalendar />} />
                    <Route path="/filings" element={<Filings />} />
                    <Route
                      path="/saved"
                      element={
                        <ProtectedRoute>
                          <SavedAnalyses />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/copilot"
                      element={
                        <ProtectedRoute>
                          <Copilot />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/market-movers" element={<MarketMovers />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route
                      path="/subscription"
                      element={
                        <ProtectedRoute>
                          <Subscription />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <Admin />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </main>
                <Footer />
              </div>
            </BrowserRouter>
          </ToastProvider>
        </CreditsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
